import { participantCsvRows, recipientCsvRows, downloadLocalCsv } from "../../../../utility/ticketingLocalExport";
import "./TicketingDashboard.scss";
import { useLocation } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFirestoreErrorTranslation } from "../../../../utility/useFirestoreErrorTranslation";
import { useMediaQuery } from "react-responsive";
import { getEventSubCollection } from "../../../../utility/getEventSubCollection";
import { getTicketRevenue } from "../../../../utility/ticketRevenue";
import InfiniteScrollList from "../../../../components/InfiniteScrollList";
import Ticket from "../../../../components/ticketing/ticket/Ticket";
import Participant from "../../../../components/ticketing/participant/Participant";
import PublicCheckoutIncidents from "../../../../components/ticketing/PublicCheckoutIncidents";
import useTicketingDashboardData from "../../../../hooks/useTicketingDashboardData";
import { ticketingDashboardRequest, dashboardChartPoints, dashboardChartItems } from "../../../../utility/ticketingDashboard";
import AttendanceSummary from "../../../../components/ticketing/AttendanceSummary";
import Avatars from "../../../../components/ticketing/avatar/Avatar";
import ModalComponent from "../../../../components/modal/Modal";
import SocialNetworks from "../../../../components/socialNetworks/SocialNetworks";
import BackImage from "../../../../images/icons/back.png";
import { read as readWorkbook, utils as xlsxUtils, writeFile as writeWorkbookFile } from "xlsx";
import { timeStampToDateTimeLocal } from "../../../../utility/dateUtils";
import PromoCodeList from "../../../../components/ticketing/promoCodeList/PromoCodeList";
import EmptyState from "../../../../components/emptyState/EmptyState";
import ENV from "../../../../env";
import CustomTicketModal from "../../../../components/CutomTicketModal/CustomTicketModal";
import getEvent from "../../../../utility/getEvent";
import ChartCard from "../../../../components/ChartCard/ChartCard";
import dayjs from "dayjs";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import GradientButton from "../../../../components/buttons/gradientButton/GradientButton";
import OutlinedPinkButton from "../../../../components/buttons/outlinedPinkButton/OutlinedPinkButton";
import getStripeTicketingReport from "../../../../utility/getStripeTicketingReport";
import importExternalParticipants from "../../../../utility/importExternalParticipants";
import recordExternalSales from "../../../../utility/recordExternalSales";
import downloadTicketingSalesReport from "../../../../utility/downloadTicketingSalesReport";
import { sendTicketingBroadcastEmail } from "../../../../utility/sendEmail";

const NB_TICKETS_REQUEST = 1;
const DAILY_EMAIL_LIMIT = 400;
const COLORS_PARTICIPANTS = ["#F28080", "#80B7F2", "#90EE90", "#FFC87C", "#C199FF"];

const createManualExternalSaleRow = () => ({
  id: `external-sale-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  ticketId: "",
  quantity: 1,
  optionSelections: [],
  error: "",
});

const createExternalSaleRequestId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `external-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const getXlsx = () => {
  if (!readWorkbook || !xlsxUtils) {
    throw new Error("XLSX module is not available");
  }
  return {
    read: readWorkbook,
    utils: xlsxUtils,
    writeFile: writeWorkbookFile,
  };
};

const decodeTextFile = (buffer) => {
  const bytes = new Uint8Array(buffer);
  const encoding = bytes[0] === 0xff && bytes[1] === 0xfe ? "utf-16le" : "utf-8";
  return new TextDecoder(encoding).decode(bytes).replace(/^\uFEFF/, "");
};

const parseDelimitedRows = (text) => {
  const firstLine = text.split(/\r?\n/).find((line) => line.trim().length > 0) || "";
  const delimiter = firstLine.includes("\t")
    ? "\t"
    : firstLine.includes(";")
      ? ";"
      : ",";
  const rows = [];
  let row = [];
  let value = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const next = text[i + 1];

    if (char === '"') {
      if (inQuotes && next === '"') {
        value += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (!inQuotes && char === delimiter) {
      row.push(value);
      value = "";
      continue;
    }

    if (!inQuotes && (char === "\n" || char === "\r")) {
      if (char === "\r" && next === "\n") i += 1;
      row.push(value);
      if (row.some((cell) => String(cell || "").trim().length > 0)) rows.push(row);
      row = [];
      value = "";
      continue;
    }

    value += char;
  }

  row.push(value);
  if (row.some((cell) => String(cell || "").trim().length > 0)) rows.push(row);
  return rows;
};

const readTabularFileRows = (file, buffer) => {
  const fileName = String(file?.name || "").toLowerCase();
  if (fileName.endsWith(".csv") || file?.type === "text/csv") {
    return parseDelimitedRows(decodeTextFile(buffer));
  }

  const xlsx = getXlsx();
  const workbook = xlsx.read(new Uint8Array(buffer), { type: "array" });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  return xlsx.utils.sheet_to_json(sheet, { header: 1 });
};

const toFiniteNumber = (val) => {
  const n = typeof val === "number" ? val : parseFloat(String(val ?? 0));
  return Number.isFinite(n) ? n : 0;
};


function TicketingDashboard({ org, onOrganizationResolved }) {
  const { t, i18n } = useTranslation("ticketingDashboard");

  const [startDate, setStartDate] = useState(dayjs().subtract(1, "week").toDate());
  const [endDate, setEndDate] = useState(dayjs().toDate());
  const [scanUser, setScanUser] = useState('');
  const [chartType, setChartType] = useState("ticketsNb");
  const [isChartLoading, setIsChartLoading] = useState(true);
  const [chartData, setChartData] = useState([]);
  const [payoutSummary, setPayoutSummary] = useState({});
  const [payouts, setPayouts] = useState([]);
  const [payoutError, setPayoutError] = useState("");
  const [isExportingPayments, setIsExportingPayments] = useState(false);
  const [isExportingPayouts, setIsExportingPayouts] = useState(false);
  const [isImportingIds, setIsImportingIds] = useState(false);
  const [isExternalImportChoiceOpen, setIsExternalImportChoiceOpen] = useState(false);
  const [isManualExternalSaleOpen, setIsManualExternalSaleOpen] = useState(false);
  const [manualExternalSaleRows, setManualExternalSaleRows] = useState([
    createManualExternalSaleRow(),
  ]);
  const [manualExternalSaleRequestId, setManualExternalSaleRequestId] = useState("");
  const [isRecordingExternalSales, setIsRecordingExternalSales] = useState(false);
  const [isGeneratingSalesReport, setIsGeneratingSalesReport] = useState(false);
  const [isBroadcastModalOpen, setIsBroadcastModalOpen] = useState(false);
  const [isSendingBroadcast, setIsSendingBroadcast] = useState(false);
  const [broadcastSubject, setBroadcastSubject] = useState("");
  const [broadcastMessage, setBroadcastMessage] = useState("");
  const [broadcastError, setBroadcastError] = useState("");
  const [lastBroadcastRequest, setLastBroadcastRequest] = useState(null);
  const [lastFailedBroadcastRecipients, setLastFailedBroadcastRecipients] = useState([]);

  const location = useLocation();
  const [event, setEvent] = useState({});
  const [eventError, setEventError] = useState(false);
  const [participantsVisible, setParticipantsVisible] = useState(false);
  const [participantsOrder, setParticipantsOrder] = useState("creationDate");
  const [participantSearch, setParticipantSearch] = useState("");
  const [ticketsHasMore, setTicketsHasMore] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [exportJob, setExportJob] = useState(null);
  const [recipientCount, setRecipientCount] = useState(null);
  const [broadcastContinuation, setBroadcastContinuation] = useState(null);
  const [customizeTicketTarget, setCustomizeTicketTarget] = useState(null);
  const allTickets = useRef([]);
  const ticketLastdoc = useRef(null);
  const importIdsInputRef = useRef(null);
  const [externalImportPreview, setExternalImportPreview] = useState(null);
  const [externalTicketMappings, setExternalTicketMappings] = useState({});
  const navigate = useNavigate();

  const searchFieldRef = useRef("");
  const [modalInformations, setModalInformations] = useState({
    content: "",
    title: "",
    type: "",
  });
  const errorTranslation = useFirestoreErrorTranslation();
  const isSmallScreen = useMediaQuery({ query: "(max-width: 1440px)" });
  const eventId = typeof location.state === 'object' ? location.state?.eventId : location.state;
  const activeEventId = useRef(eventId);
  activeEventId.current = eventId;
  const eventOrgId = event.id === eventId ? String(event.orgId || '') : '';
  const organizationMatches = Boolean(eventOrgId && String(org?.id || '') === eventOrgId);
  useEffect(() => {
    if (eventOrgId && !organizationMatches) onOrganizationResolved?.(eventOrgId);
  }, [eventOrgId, organizationMatches, onOrganizationResolved]);

  const TICKETING_URL = `${ENV.hostname}/events/${eventId}`;
  const IFRAME_CODE = `<iframe src="${TICKETING_URL}?hideLayout=true" title="${event?.name || "Ticketing"}" style="width:100%;min-height:100vh;border:0;display:block" scrolling="auto"></iframe>`;

  const dashboard = useTicketingDashboardData({ eventId, orgId: organizationMatches ? org.id : undefined,
    visible: participantsVisible, sort: participantsOrder, search: participantSearch });
  const { summary, ready, participants } = dashboard;
  const participantsNb = ready ? summary.participantsCount : null;
  const ticketsSalesNumber = summary?.ticketsDistributed || 0;
  const paidCheckoutTicketsNumber = summary?.paidCheckoutTickets || 0;
  const paidCheckoutTicketsById = summary?.paidCheckoutTicketsById || {};
  const paidCheckoutOptionsByTicketId = summary?.paidCheckoutOptionsByTicketId || {};
  const ticketsScannedNumber = summary?.ticketsScanned || 0;
  const receivedTicketSalesAmount = summary?.collectedMoney || 0;
  const ticketsTheoreticalAmount = summary?.theoreticalRevenue || 0;
  const [refreshKey, setRefreshKey] = useState(0);
  const fetchParticipants = async (id) => {
    if (id) {
      await dashboard.updateParticipant(id);
      // Updating one row must not wait for a new full statistics pass.
      dashboard.loadSummary();
      fetchEvent();
    } else {
      await Promise.all([dashboard.refresh(), fetchEvent()]);
    }
    setRefreshKey((value) => value + 1);
  };
  const chartSequence = useRef(0);
  const currentScope = useRef('');
  currentScope.current = `${eventId}:${org?.id}`;
  useEffect(() => {
    setScanUser('');
    setExportJob(null); setBroadcastContinuation(null); setLastFailedBroadcastRecipients([]);
    setLastBroadcastRequest(null); setIsBroadcastModalOpen(false);
  }, [eventId, org?.id]);


  useEffect(() => {
    if (!isBroadcastModalOpen || !ready) return undefined;
    let active = true;
    setRecipientCount(null);
    if (dashboard.listReady) {
      setRecipientCount(recipientCsvRows(dashboard.allParticipants || []).length);
      return () => { active = false; };
    }
    ticketingDashboardRequest('recipients', { eventId, orgId: org?.id })
      .then((result) => { if (active) setRecipientCount(result.count); })
      .catch(() => { if (active) setBroadcastError(t('performance.loadError')); });
    return () => { active = false; };
  }, [isBroadcastModalOpen, ready, eventId, org?.id, t, dashboard.listReady, dashboard.allParticipants]);

  async function startExport(kind = 'participants') {
    if (exportJob?.status === 'requesting') return;
    setExportJob({ status: 'requesting', kind });
    const scope = currentScope.current;
    try {
      const result = await ticketingDashboardRequest('exports', { eventId, orgId: org?.id, kind, language: i18n?.language || "fr" }, 'POST');
      if (scope === currentScope.current) setExportJob(result);
    } catch { if (scope === currentScope.current) setExportJob({ status: 'failed', kind }); }
  }

  async function checkExportStatus() {
    if (!exportJob?.id) return;
    const scope = currentScope.current;
    try {
      const result = await ticketingDashboardRequest('exports', { eventId, orgId: org?.id, id: exportJob.id });
      if (scope === currentScope.current) setExportJob(result);
    } catch { if (scope === currentScope.current) setExportJob(job => ({ ...job, status: 'failed' })); }
  }

  useEffect(() => {
    if (eventId) {
      fetchEvent();
      fetchTickets();
    } else {
      navigate(-1);
    }
  }, [eventId]);

  async function fetchEvent() {
    try {
      setEventError(false);
      const record = await getEvent(eventId);
      if (activeEventId.current !== eventId) return;
      if (!record?.orgId) throw new Error('Event organization unavailable');
      setEvent({ ...record, id: eventId });
    } catch (error) {
      if (activeEventId.current === eventId) setEventError(true);
      console.error(error);
    }
  }

  async function fetchTickets() {
    try {
      const ticketsData = await getEventSubCollection({
        collectionName: "Events",
        docId: eventId,
        subCollectionName: "Tickets",
      });

      if (activeEventId.current !== eventId) return;
      if (ticketsData.data.length > 0) {
        allTickets.current = ticketsData.data;
        setTickets(ticketsData.data);
        ticketLastdoc.current = ticketsData.data.length;
        setTicketsHasMore(false);
      } else {
        allTickets.current = [];
        setTickets([]);
        setTicketsHasMore(false);
      }
    } catch (error) {
      setModalInformations({
        title: t("errorFetchTickets"),
        content: errorTranslation(error),
      });
      console.error(t("errorFetchTickets"), error);
    }
  }

  function findParticipants(e) { setParticipantSearch(e.target.value); }

  function loadTickets() {
    if (ticketsHasMore) {
      const start = tickets.length;
      const end = ticketLastdoc.current;

      const newSlice = allTickets.current.slice(start, end);
      setTickets((prev) => [...prev, ...newSlice]);

      ticketLastdoc.current += NB_TICKETS_REQUEST;
      setTicketsHasMore(ticketLastdoc.current < allTickets.current.length);
    }
  }

  function exportParticipantsToCSV() {
    if (!dashboard.listReady) return;
    const rows = participantCsvRows(dashboard.allParticipants || []);
    downloadLocalCsv(rows, ["id", "Firstname", "Name", "Email", "createdAt", "isRead", "numberOfScan", "quantity"], `Participants_List_Event_${eventId}.csv`);
  }

  function exportTicketsToXlsx() {
    const rows = allTickets.current.map((ticket) => {
      const ticketFields = Object.entries(ticket || {}).reduce((fields, [key, value]) => {
        if (["image", "customTicketConfiguration"].includes(key)) return fields;
        fields[key] = value && typeof value === "object" ? JSON.stringify(value) : value ?? "";
        return fields;
      }, {});
      const amountSold = Number(ticket.amountSold || 0);

      return {
        ...ticketFields,
        eventId,
        eventName: event?.name || "",
        ticketId: ticket.id || "",
        sold: amountSold,
        available: Math.max(0, Number(ticket.numAvailable || 0) - amountSold),
        revenue: getTicketRevenue(ticket),
        availableFrom: ticket.availableFrom ? timeStampToDateTimeLocal(Number(ticket.availableFrom)) : "",
        availableTo: ticket.availableTo ? timeStampToDateTimeLocal(Number(ticket.availableTo)) : "",
      };
    });

    const headers = Array.from(new Set(rows.flatMap((row) => Object.keys(row))));
    exportRowsAsCsv(rows, headers, `Tickets_Event_${eventId}.csv`);
  }

  function handleOrder(orderAttribute) { setParticipantsOrder(orderAttribute); }

  async function onCustomizationModalClose() {
    setCustomizeTicketTarget(null);
    await Promise.all([fetchEvent(), fetchTickets()]);
  }

  async function handleParticipantCancelled({ participantId, participantName, inventoryWarnings = [] }) {
    await Promise.all([fetchParticipants(participantId), fetchTickets()]);
    setModalInformations({
      title: t("participantCancellationSuccessTitle"),
      content: inventoryWarnings.length
        ? `${t("participantCancellationSuccessMessage", { participant: participantName })}\n${t(
            "participantCancellationStockWarning",
          )}`
        : t("participantCancellationSuccessMessage", { participant: participantName }),
      type: "",
    });
  }

  async function groupParticipantsData(chartDataType = chartType) {
    const sequence = ++chartSequence.current;
    setIsChartLoading(true);
    try {
      const from = Math.floor(startDate.getTime() / 3600000) * 3600000;
      const to = endDate.getTime();
      const result = { items: dashboardChartItems(summary, chartDataType, scanUser).filter(item => item.timestamp >= from && item.timestamp <= to) };
      if (sequence !== chartSequence.current) return;
      setChartData(dashboardChartPoints(result.items));
    } catch {
      if (sequence === chartSequence.current) setModalInformations({ title: t('errorFetchParticipants'), content: t('performance.loadError') });
    } finally { if (sequence === chartSequence.current) setIsChartLoading(false); }
  }
  const chartRefresh = useRef();
  chartRefresh.current = groupParticipantsData;
  useEffect(() => {
    if (ready && chartType !== 'payouts') chartRefresh.current(chartType);
    return () => { chartSequence.current++; };
  }, [summary?.generatedAt, ready, chartType, scanUser]);

  function onChartTypeChange(chartDataType) {
    setChartType(chartDataType);
    if (chartDataType === "payouts") {
      fetchPayoutSummary();
      return;
    }
    groupParticipantsData(chartDataType);
  }

  function onValidateChartDates() {
    if (chartType === "payouts") {
      fetchPayoutSummary();
    } else {
      groupParticipantsData();
    }
  }

  async function fetchPayoutSummary() {
    if (!org?.stripeId) {
      setPayoutError(t("errorMissingStripe"));
      return;
    }

    setIsChartLoading(true);
    setPayoutError("");
    try {
      const report = await getStripeTicketingReport({
        eventId,
        from: startDate.getTime(),
        to: endDate.getTime(),
      });
      const payoutRows = [...(report?.payouts || [])].sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
      );
      const totals = payoutRows.reduce((summary, payout) => {
        const currency = String(payout.currency || "EUR").toUpperCase();
        const status = payout.isFuturePayout
          ? "future"
          : String(payout.status || "unknown").toLowerCase();
        if (!summary[currency]) summary[currency] = {};
        summary[currency][status] = (summary[currency][status] || 0) + toFiniteNumber(payout.amount);
        return summary;
      }, {});
      setPayoutSummary(totals);
      setPayouts(payoutRows);
    } catch (error) {
      console.error("Failed to fetch payout summary", error);
      setPayoutError(error.message || t("errorFetchStripeReport"));
      setPayoutSummary({});
      setPayouts([]);
    } finally {
      setIsChartLoading(false);
    }
  }

  const copyCodeHandler = (e) => {
    navigator.clipboard.writeText(IFRAME_CODE);
    e.target.textContent = t("copied");
  };

  function exportRowsAsCsv(rows, headers, filename, { financialNumbers = false } = {}) {
    const xlsx = getXlsx();
    const worksheet = xlsx.utils.json_to_sheet(rows, { header: headers });
    const csvContent = xlsx.utils.sheet_to_csv(worksheet, {
      FS: financialNumbers ? ";" : ",",
      RS: "\r\n",
    });
    // A semicolon-separated CSV with comma decimals lets Excel and Numbers in
    // French/Belgian locales recognize Stripe amounts as numeric values.
    const blob = new Blob(["\uFEFF", csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  function exportEmailRecipientsToCSV(recipients, filename) {
    const rows = recipients.map((recipient) => ({
      email: recipient.email || "",
      name: recipient.name || "",
      error: recipient.error || "",
    }));
    const headers = rows.some((row) => row.error) ? ["email", "name", "error"] : ["email", "name"];

    exportRowsAsCsv(rows, headers, filename);
  }

  function exportUniqueBroadcastEmailsToCSV() {
    if (!dashboard.listReady) { startExport('recipients'); return; }
    const rows = recipientCsvRows(dashboard.allParticipants || []);
    downloadLocalCsv(rows, ["email", "name"], `Emails_Event_${eventId}.csv`);
  }

  function exportFailedBroadcastEmailsToCSV() {
    exportEmailRecipientsToCSV(
      lastFailedBroadcastRecipients,
      `Ticketing_Failed_Emails_${eventId}.csv`,
    );
  }

  async function exportTicketPaymentsToCSV() {
    if (!org?.stripeId) {
      setModalInformations({
        title: t("errorFetchStripeReportTitle"),
        content: t("errorMissingStripe"),
      });
      return;
    }

    try {
      setIsExportingPayments(true);

      const report = await getStripeTicketingReport({
        eventId,
        from: startDate.getTime(),
        to: endDate.getTime(),
      });

      const formatFinancialAmount = (amount) => {
        const numericAmount = Number(amount ?? 0);
        return Number.isFinite(numericAmount)
          ? numericAmount.toFixed(2).replace(".", ",")
          : "0,00";
      };
      const rows = (report?.payments || []).map((payment) => ({
        orderReference: payment.orderReference || "",
        date: payment.date || "",
        status: payment.status || "",
        grossPaymentAmount: formatFinancialAmount(payment.grossPaymentAmount),
        stripeProcessingFee: formatFinancialAmount(payment.stripeProcessingFee),
        applicationFee: formatFinancialAmount(payment.applicationFee),
        netRevenue: formatFinancialAmount(payment.netRevenue),
        currency: payment.currency || "",
        checkoutSessionId: payment.checkoutSessionId || "",
        paymentIntentId: payment.paymentIntentId || "",
      }));

      exportRowsAsCsv(
        rows,
        [
          "orderReference",
          "date",
          "status",
          "grossPaymentAmount",
          "stripeProcessingFee",
          "applicationFee",
          "netRevenue",
          "currency",
          "checkoutSessionId",
          "paymentIntentId",
        ],
        `Ticketing_Payments_${eventId}.csv`,
        { financialNumbers: true },
      );
    } catch (error) {
      setModalInformations({
        title: t("errorFetchStripeReportTitle"),
        content: error.message || t("errorFetchStripeReport"),
      });
      console.error(t("errorFetchStripeReport"), error);
    } finally {
      setIsExportingPayments(false);
    }
  }

  async function exportStripePayoutsToCSV() {
    if (!org?.stripeId) {
      setModalInformations({
        title: t("errorFetchStripeReportTitle"),
        content: t("errorMissingStripe"),
      });
      return;
    }

    try {
      setIsExportingPayouts(true);

      const report = await getStripeTicketingReport({
        eventId,
        from: startDate.getTime(),
        to: endDate.getTime(),
      });

      const rows = (report?.payouts || []).map((payout) => ({
        payoutId: payout.payoutId || "",
        amount: payout.amount ?? 0,
        currency: payout.currency || "",
        date: payout.date || "",
        status: payout.isFuturePayout ? t("payoutStatus_future") : payout.status || "",
        bankArrivalDate: payout.bankArrivalDate || "",
        source: payout.isFutureBalance ? t("payoutFutureBalance") : "Stripe",
      }));

      exportRowsAsCsv(
        rows,
        ["payoutId", "amount", "currency", "date", "status", "bankArrivalDate", "source"],
        `Stripe_Payouts_${eventId}.csv`,
      );
    } catch (error) {
      setModalInformations({
        title: t("errorFetchStripeReportTitle"),
        content: error.message || t("errorFetchStripeReport"),
      });
      console.error(t("errorFetchStripeReport"), error);
    } finally {
      setIsExportingPayouts(false);
    }
  }

  const normalizeImportHeader = (value = "") =>
    String(value)
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

  function parseExternalParticipantsRows(rows) {
    if (!Array.isArray(rows) || rows.length === 0) return [];
    const headers = (rows[0] || []).map(normalizeImportHeader);
    const colIndex = (candidates) => {
      for (const key of candidates) {
        const idx = headers.indexOf(normalizeImportHeader(key));
        if (idx >= 0) return idx;
      }
      return -1;
    };

    const barcodeIdx = colIndex(["Codes-barres", "Barcode", "Bar code"]);
    const billetwebTicketIdx = colIndex(["Billet"]);
    const billetwebTicketTypeIdx = colIndex(["Tarif"]);
    const isBilletweb = barcodeIdx >= 0 && billetwebTicketIdx >= 0 && billetwebTicketTypeIdx >= 0;

    if (isBilletweb) {
      const orderIdx = colIndex(["Commande", "Order number", "Order"]);
      const firstnameIdx = colIndex(["Prénom", "Guest first name", "First name", "Firstname"]);
      const lastnameIdx = colIndex(["Nom", "Guest last name", "Last name", "Name", "Lastname"]);
      const emailIdx = colIndex(["Email", "E-mail"]);
      const paidIdx = colIndex(["Payé", "Paye", "Paid"]);
      const validIdx = colIndex(["Valide", "Valid"]);
      let ignoredInvalidCount = 0;

      const participants = rows
        .slice(1)
        .reduce((items, row) => {
          const externalRawQrValue = String(row?.[barcodeIdx] || "").trim();
          const isPaid = paidIdx < 0 || String(row?.[paidIdx] || "").trim().toLowerCase() === "oui";
          const isValid = validIdx < 0 || String(row?.[validIdx] || "").trim().toLowerCase() === "oui";

          if (!externalRawQrValue || !isPaid || !isValid) {
            ignoredInvalidCount += 1;
            return items;
          }

          items.push({
            externalOrderNumber: orderIdx >= 0 ? String(row?.[orderIdx] || "").trim() : "",
            firstname: firstnameIdx >= 0 ? String(row?.[firstnameIdx] || "").trim() : "",
            lastname: lastnameIdx >= 0 ? String(row?.[lastnameIdx] || "").trim() : "",
            email: emailIdx >= 0 ? String(row?.[emailIdx] || "").trim() : "",
            externalTicketType: String(row?.[billetwebTicketTypeIdx] || "").trim(),
            externalTicketNumber: String(row?.[billetwebTicketIdx] || "").trim(),
            externalRawQrValue,
          });
          return items;
        }, []);

      const ticketTypes = [
        ...new Set(participants.map((item) => item.externalTicketType).filter(Boolean)),
      ];
      const duplicateTicketNumbers = participants.length - new Set(
        participants.map((item) => item.externalRawQrValue.toLowerCase()),
      ).size;

      return {
        provider: "billetweb",
        participants,
        ticketTypes,
        duplicateTicketNumbers,
        ignoredInvalidCount,
      };
    }

    const orderIdx = colIndex(["Order number", "Order"]);
    const firstnameIdx = colIndex(["Guest first name", "First name", "Firstname"]);
    const lastnameIdx = colIndex(["Guest last name", "Last name", "Name", "Lastname"]);
    const emailIdx = colIndex(["Email", "E-mail"]);
    const ticketTypeIdx = colIndex(["Ticket type", "Ticket"]);
    const ticketNumberIdx = colIndex(["Ticket number", "Participant ID", "participantId", "id"]);
    const paymentStatusIdx = colIndex(["Payment status"]);

    if (ticketNumberIdx < 0 || ticketTypeIdx < 0) {
      throw new Error(t("externalImportMissingColumns"));
    }

    const participants = rows
      .slice(1)
      .map((row) => ({
        externalOrderNumber: orderIdx >= 0 ? String(row?.[orderIdx] || "").trim() : "",
        firstname: firstnameIdx >= 0 ? String(row?.[firstnameIdx] || "").trim() : "",
        lastname: lastnameIdx >= 0 ? String(row?.[lastnameIdx] || "").trim() : "",
        email: emailIdx >= 0 ? String(row?.[emailIdx] || "").trim() : "",
        externalTicketType: ticketTypeIdx >= 0 ? String(row?.[ticketTypeIdx] || "").trim() : "",
        externalTicketNumber:
          ticketNumberIdx >= 0 ? String(row?.[ticketNumberIdx] || "").trim() : "",
        paymentStatus: paymentStatusIdx >= 0 ? String(row?.[paymentStatusIdx] || "").trim() : "",
      }))
      .filter((item) => item.externalTicketNumber.length > 0);

    const ticketTypes = [
      ...new Set(participants.map((item) => item.externalTicketType).filter(Boolean)),
    ];
    const duplicateTicketNumbers = participants.length - new Set(
      participants.map((item) => item.externalTicketNumber.toLowerCase()),
    ).size;

    return {
      provider: "wix",
      participants,
      ticketTypes,
      duplicateTicketNumbers,
      ignoredInvalidCount: 0,
    };
  }

  async function handleExternalImportFile(eventInput) {
    const file = eventInput.target.files?.[0];
    if (!file) return;
    importIdsInputRef.current.value = "";

    setIsImportingIds(true);
    try {
      const buffer = await file.arrayBuffer();
      const rows = readTabularFileRows(file, buffer);
      const preview = parseExternalParticipantsRows(rows);

      if (!preview.participants.length) {
        throw new Error(t("externalImportNoRows"));
      }

      const defaultTicketId = tickets[0]?.id || "";
      const initialMappings = {};
      preview.ticketTypes.forEach((ticketType) => {
        initialMappings[ticketType] =
          tickets.find(
            (ticket) =>
              String(ticket.name || "").trim().toLowerCase() ===
              String(ticketType || "").trim().toLowerCase(),
          )?.id || defaultTicketId;
      });

      setExternalTicketMappings(initialMappings);
      setExternalImportPreview({
        ...preview,
        fileName: file.name,
        externalImportId: `${preview.provider}_${eventId}_${Date.now()}`,
      });
    } catch (error) {
      setModalInformations({
        title: t("externalImportTitle"),
        content: error?.message || t("externalImportError"),
      });
    } finally {
      setIsImportingIds(false);
    }
  }

  async function confirmExternalImport() {
    if (!externalImportPreview) return;
    const missingMappings = externalImportPreview.ticketTypes.filter(
      (ticketType) => !externalTicketMappings[ticketType],
    );
    if (missingMappings.length > 0) {
      setModalInformations({
        title: t("externalImportTitle"),
        content: t("externalImportMappingRequired"),
      });
      return;
    }

    setIsImportingIds(true);
    try {
      const participants = externalImportPreview.participants.map((participant) => ({
        ...participant,
        ticketId: externalTicketMappings[participant.externalTicketType],
      }));

      const result = await importExternalParticipants({
        eventId,
        orgId: org?.id,
        provider: externalImportPreview.provider,
        externalImportId: externalImportPreview.externalImportId,
        ignoredInvalidCount: externalImportPreview.ignoredInvalidCount || 0,
        participants,
      });

      setExternalImportPreview(null);
      setExternalTicketMappings({});
      setModalInformations({
        title: t("externalImportResultTitle"),
        content: `${t("externalImportCreated")}: ${result.createdCount}\n${t(
          "externalImportExisting",
        )}: ${result.existingCount || 0}\n${t("externalImportDuplicates")}: ${
          result.duplicateInFileCount || 0
        }\n${t("externalImportInvalidRowsIgnored")}: ${result.ignoredInvalidCount || 0}\n${t(
          "externalImportFailed",
        )}: ${result.failedCount || 0}`,
      });
      await Promise.all([fetchParticipants(), fetchTickets()]);
    } catch (error) {
      setModalInformations({
        title: t("externalImportTitle"),
        content: error?.message || t("externalImportError"),
      });
    } finally {
      setIsImportingIds(false);
    }
  }

  function openManualExternalSales() {
    setIsExternalImportChoiceOpen(false);
    setManualExternalSaleRows([createManualExternalSaleRow()]);
    setManualExternalSaleRequestId(createExternalSaleRequestId());
    setIsManualExternalSaleOpen(true);
  }

  function updateManualExternalSaleRow(rowId, patch) {
    setManualExternalSaleRows((rows) =>
      rows.map((row) => (row.id === rowId ? { ...row, ...patch, error: "" } : row)),
    );
  }

  function selectManualExternalTicket(rowId, ticketId) {
    updateManualExternalSaleRow(rowId, { ticketId, optionSelections: [] });
  }

  function toggleManualExternalOption(rowId, group, valueId) {
    setManualExternalSaleRows((rows) =>
      rows.map((row) => {
        if (row.id !== rowId) return row;
        const selectedInGroup = row.optionSelections.filter(
          (selection) => selection.groupId === group.id,
        );
        const isSelected = selectedInGroup.some((selection) => selection.valueId === valueId);
        let optionSelections;
        if (group.selectionType === "multi") {
          optionSelections = isSelected
            ? row.optionSelections.filter(
                (selection) => !(selection.groupId === group.id && selection.valueId === valueId),
              )
            : [...row.optionSelections, { groupId: group.id, valueId }];
        } else {
          optionSelections = [
            ...row.optionSelections.filter((selection) => selection.groupId !== group.id),
            { groupId: group.id, valueId },
          ];
        }
        return { ...row, optionSelections, error: "" };
      }),
    );
  }

  function validateManualExternalSales() {
    let isValid = true;
    const nextRows = manualExternalSaleRows.map((row) => {
      const ticket = allTickets.current.find((item) => item.id === row.ticketId);
      let error = "";
      if (!ticket) {
        error = t("externalSaleTicketRequired");
      } else if (!Number.isInteger(Number(row.quantity)) || Number(row.quantity) <= 0) {
        error = t("externalSaleQuantityInvalid");
      } else {
        const missingRequiredOption = (ticket.options || []).find(
          (group) =>
            group.required &&
            !row.optionSelections.some((selection) => selection.groupId === group.id),
        );
        if (missingRequiredOption) error = t("externalSaleOptionRequired", { name: missingRequiredOption.name });
      }
      if (error) isValid = false;
      return { ...row, error };
    });
    setManualExternalSaleRows(nextRows);
    return isValid;
  }

  async function confirmManualExternalSales() {
    if (!validateManualExternalSales() || !org?.id) return;

    setIsRecordingExternalSales(true);
    try {
      const result = await recordExternalSales({
        eventId,
        orgId: org.id,
        requestId: manualExternalSaleRequestId || createExternalSaleRequestId(),
        rows: manualExternalSaleRows.map((row) => ({
          ticketId: row.ticketId,
          quantity: Number(row.quantity),
          optionSelections: row.optionSelections,
        })),
      });
      setIsManualExternalSaleOpen(false);
      setManualExternalSaleRows([createManualExternalSaleRow()]);
      setManualExternalSaleRequestId("");
      setModalInformations({
        title: t("externalSaleResultTitle"),
        content: `${t("externalSaleCreated")}: ${result.createdCount}\n${t("externalSaleExisting")}: ${result.existingCount}`,
      });
      await Promise.all([fetchParticipants(), fetchTickets()]);
    } catch (error) {
      setModalInformations({
        title: t("externalSaleTitle"),
        content: error?.message || t("externalSaleError"),
      });
    } finally {
      setIsRecordingExternalSales(false);
    }
  }

  async function generateSalesReport() {
    setIsGeneratingSalesReport(true);
    try {
      const promoCodesData = await getEventSubCollection({
        collectionName: "Events",
        docId: eventId,
        subCollectionName: "PromoCodes",
      });
      const ticketNameById = new Map(
        allTickets.current.map((ticket) => [String(ticket.id), ticket.name || t("promo.unknownTicket")]),
      );
      const revenueByTicketId = new Map(Object.entries(summary?.theoreticalByTicketId || {}));
      const reportTickets = allTickets.current.map((ticket) => ({
        id: ticket.id,
        name: ticket.name || t("tickets"),
        amountSold: toFiniteNumber(ticket.amountSold),
        revenue: revenueByTicketId.get(String(ticket.id)) || 0,
        options: (ticket.options || []).flatMap((group) =>
          (group.values || [])
            .filter((value) => toFiniteNumber(value.amountSold) > 0)
            .map((value) => ({
              id: `${group.id}-${value.id}`,
              groupName: group.name || t("salesReportOptions"),
              label: value.label,
              amountSold: toFiniteNumber(value.amountSold),
              priceDelta: toFiniteNumber(value.priceDelta),
            })),
        ),
      }));
      const promoCodes = promoCodesData.data.map((promo) => {
        const discountsText = (promo.ticketDiscounts || [])
          .map((discount) => {
            const value = Number(discount.discountValue ?? discount.discountPercentage ?? 0);
            return `${ticketNameById.get(String(discount.ticketId)) || t("promo.unknownTicket")}: ${value}${discount.isPercentage === false ? "€" : "%"}`;
          })
          .join(" · ");
        const reservationText = Object.entries(promo.reservedTicketQuantities || {})
          .filter(([, value]) => Number(value) > 0)
          .map(([ticketId, value]) => `${ticketNameById.get(String(ticketId)) || t("promo.unknownTicket")}: ${value}`)
          .join(" · ");
        return { ...promo, discountsText, reservationText };
      });
      const optionsSold = reportTickets.reduce(
        (total, ticket) => total + ticket.options.reduce((sum, option) => sum + option.amountSold, 0),
        0,
      );
      const locale = t("pdfLocale", { defaultValue: "fr-BE" });
      await downloadTicketingSalesReport({
        report: {
          eventName: event.name || event.title || t("yourTickets"),
          organizationName: org?.organization || org?.legalName || "Comitty",
          organizationLogo:
            org?.logo || org?.logoUrl || org?.imageUrl || org?.avatar || org?.image || null,
          generatedAt: new Intl.DateTimeFormat(locale, { dateStyle: "long", timeStyle: "short" }).format(new Date()),
          locale,
          participantsCount: participantsNb,
          paidCheckoutTickets: paidCheckoutTicketsNumber,
          ticketsDistributed: ticketsSalesNumber,
          ticketsScanned: ticketsScannedNumber,
          theoreticalRevenue: ticketsTheoreticalAmount,
          collectedMoney: receivedTicketSalesAmount,
          optionsSold,
          tickets: reportTickets,
          promoCodes,
        },
        labels: {
          title: t("salesReportTitle"),
          generatedAt: t("salesReportGeneratedAt"),
          summary: t("salesReportSummary"),
          theoreticalRevenue: t("theoreticalRevenue"),
          collectedMoney: t("collectedMoney"),
          participants: t("participants"),
          paidCheckoutTickets: t("paidCheckoutTickets"),
          ticketsDistributed: t("ticketsDistributed"),
          ticketsScanned: t("scannedTicketsCount"),
          optionsSold: t("salesReportOptionsSold"),
          ticketDetails: t("salesReportTicketDetails"),
          ticket: t("salesReportTicket"),
          sold: t("salesReportSold"),
          revenue: t("revenue"),
          options: t("salesReportOptions"),
          noOptions: t("salesReportNoOptions"),
          promoDetails: t("salesReportPromoDetails"),
          promo: t("promo.code"),
          usage: t("promo.usage"),
          discounts: t("promo.discount"),
          reservation: t("promo.reserveTicketCapacity"),
          active: t("promo.active"),
          inactive: t("promo.inactive"),
          partner: t("partnerLink"),
          excludeFromTheoreticalRevenue: t("promo.excludeFromTheoreticalRevenue"),
          unlimited: t("promo.unlimited"),
          none: "-",
          noPromoCodes: t("promo.noPromoCodes"),
          footer: "Comitty",
        },
      });
    } catch (error) {
      console.error("Could not generate sales report:", error);
      setModalInformations({ title: t("salesReportTitle"), content: t("salesReportError") });
    } finally {
      setIsGeneratingSalesReport(false);
    }
  }

  async function runBroadcastEmails({ recipients, subject, message, closeComposerOnSuccess, selectionCursor = null }) {
    setIsSendingBroadcast(true);

    try {
      const result = await sendTicketingBroadcastEmail({
        props: {
          orgId: org?.id,
          eventId,
          recipients,
          selectParticipants: !recipients,
          cursor: !recipients ? selectionCursor : undefined,
          subject,
          message,
          eventName: event?.name || "",
          eventLink: TICKETING_URL,
          organizationName: org?.organization || org?.name || "",
        },
      });

      if (!recipients) setBroadcastContinuation(result.remainingRecipientCount > 0
        ? { cursor: result.nextCursor, count: result.remainingRecipientCount, subject, message } : null);
      const sentCount = Number(result?.sentCount || 0);
      const failedCount = Number(result?.failedCount || 0);
      const failedRecipients = Array.isArray(result?.failedRecipients)
        ? result.failedRecipients
        : [];

      setLastBroadcastRequest({ recipients: failedRecipients, subject, message });
      setLastFailedBroadcastRecipients(failedRecipients);

      if (closeComposerOnSuccess) {
        setIsBroadcastModalOpen(false);
        setBroadcastSubject("");
        setBroadcastMessage("");
      }

      setModalInformations({
        title: failedCount ? t("broadcastPartialTitle") : t("broadcastSuccessTitle"),
        content: failedCount
          ? t("broadcastPartialMessage", { sent: sentCount, failed: failedCount })
          : t("broadcastSuccessMessage", { sent: sentCount }),
        type: "broadcastResult",
      });
    } catch (error) {
      let errorMessage = error?.message || t("broadcastUnexpectedError");

      if (error?.status === 429) {
        errorMessage = t("broadcastLimitMessage", {
          limit: error?.data?.limit || DAILY_EMAIL_LIMIT,
          remaining: error?.data?.remaining || 0,
          contact: "info@comitty.be",
        });
      }

      if (closeComposerOnSuccess) {
        setBroadcastError(errorMessage);
      } else {
        setModalInformations({
          title: t("broadcastPartialTitle"),
          content: errorMessage,
          type: "broadcastResult",
        });
      }
    } finally {
      setIsSendingBroadcast(false);
    }
  }

  async function sendBroadcastEmails() {
    if (isSendingBroadcast) return;

    const subject = broadcastSubject.trim();
    const message = broadcastMessage.trim();

    if (!subject || !message) {
      setBroadcastError(t("broadcastValidationMessage"));
      return;
    }

    if (!recipientCount) { setBroadcastError(t("broadcastNoRecipientsMessage")); return; }
    const recipients = undefined;

    setBroadcastError("");
    await runBroadcastEmails({ recipients, subject, message, closeComposerOnSuccess: true });
  }

  async function retryFailedBroadcastEmails() {
    if (isSendingBroadcast || !lastBroadcastRequest?.recipients?.length) return;

    setModalInformations({ title: "", content: "", type: "" });
    setBroadcastError("");
    await runBroadcastEmails({
      recipients: lastBroadcastRequest.recipients,
      subject: lastBroadcastRequest.subject,
      message: lastBroadcastRequest.message,
      closeComposerOnSuccess: false,
    });
  }

  function renderBroadcastResultActions() {
    if (!lastFailedBroadcastRecipients.length && !broadcastContinuation) return null;

    return (
      <div className="broadcastResultActions">
        {broadcastContinuation && <>
          <p>{t('performance.remainingRecipients', { count: broadcastContinuation.count })}</p>
          <button className="broadcastSecondaryButton" disabled={isSendingBroadcast} onClick={() => runBroadcastEmails({
            subject: broadcastContinuation.subject, message: broadcastContinuation.message,
            selectionCursor: broadcastContinuation.cursor, closeComposerOnSuccess: false,
          })}>{t('performance.next')}</button>
        </>}
        <button
          type="button"
          className="broadcastSecondaryButton"
          onClick={exportFailedBroadcastEmailsToCSV}
          disabled={isSendingBroadcast}
        >
          {t("broadcastExportFailedCsv")}
        </button>
        <button
          type="button"
          className="broadcastPrimaryButton"
          onClick={retryFailedBroadcastEmails}
          disabled={isSendingBroadcast || !lastFailedBroadcastRecipients.length}
        >
          {isSendingBroadcast ? t("broadcastSending") : t("broadcastRetryFailed")}
        </button>
      </div>
    );
  }

  function renderExternalImportPreview() {
    if (!externalImportPreview) return null;

    const sampleRows = externalImportPreview.participants.slice(0, 5);

    return (
      <div className="externalImportPreview">
        <div className="externalImportSummary">
          <p>
            <strong>{externalImportPreview.fileName}</strong>
          </p>
          <p>
            {t("externalImportProvider")}: <strong>{externalImportPreview.provider === "billetweb" ? "Billetweb" : "Wix"}</strong>
          </p>
          {externalImportPreview.ignoredInvalidCount > 0 ? (
            <p className="externalImportWarning">
              {t("externalImportInvalidRowsIgnored", {
                count: externalImportPreview.ignoredInvalidCount,
              })}
            </p>
          ) : null}
          <p>
            {t("externalImportParticipantsDetected")}:{" "}
            <strong>{externalImportPreview.participants.length}</strong>
          </p>
          {externalImportPreview.duplicateTicketNumbers > 0 ? (
            <p className="externalImportWarning">
              {t("externalImportDuplicateInFile", {
                count: externalImportPreview.duplicateTicketNumbers,
              })}
            </p>
          ) : null}
        </div>

        <div className="externalImportMappings">
          <p className="externalImportSectionTitle">{t("externalImportMappingTitle")}</p>
          {externalImportPreview.ticketTypes.map((ticketType) => (
            <label key={ticketType} className="externalImportMappingRow">
              <span>{ticketType}</span>
              <select
                value={externalTicketMappings[ticketType] || ""}
                onChange={(event) =>
                  setExternalTicketMappings((prev) => ({
                    ...prev,
                    [ticketType]: event.target.value,
                  }))
                }
                disabled={isImportingIds}
              >
                <option value="">{t("externalImportSelectTicket")}</option>
                {tickets.map((ticket) => (
                  <option key={ticket.id} value={ticket.id}>
                    {ticket.name}
                  </option>
                ))}
              </select>
            </label>
          ))}
        </div>

        <div className="externalImportRows">
          <p className="externalImportSectionTitle">{t("externalImportPreviewTitle")}</p>
          <div className="externalImportTable">
            <div className="externalImportTableHeader">
              <span>{t("externalImportTicketNumber")}</span>
              <span>{t("externalImportName")}</span>
              <span>{t("externalImportTicketType")}</span>
            </div>
            {sampleRows.map((row) => (
              <div className="externalImportTableRow" key={row.externalTicketNumber}>
                <span>
                  {externalImportPreview.provider === "billetweb"
                    ? row.externalRawQrValue
                    : `wix_${row.externalTicketNumber}`}
                </span>
                <span>{`${row.firstname} ${row.lastname}`.trim() || "-"}</span>
                <span>{row.externalTicketType || "-"}</span>
              </div>
            ))}
          </div>
        </div>

        {isImportingIds ? (
          <div className="externalImportLoader" role="status" aria-live="polite">
            <span className="externalImportSpinner" />
            <span>{t("importing")}</span>
          </div>
        ) : null}
      </div>
    );
  }

  function renderExternalImportChoice() {
    return (
      <div className="externalImportChoice">
        <p>{t("externalImportChoiceDescription")}</p>
        <div className="externalImportChoiceCards">
          <button
            type="button"
            className="externalImportChoiceCard"
            onClick={() => {
              setIsExternalImportChoiceOpen(false);
              window.setTimeout(() => importIdsInputRef.current?.click(), 0);
            }}
          >
            <strong>{t("externalImportCsvChoice")}</strong>
            <span>{t("externalImportCsvChoiceDescription")}</span>
          </button>
          <button
            type="button"
            className="externalImportChoiceCard"
            onClick={openManualExternalSales}
          >
            <strong>{t("externalSaleChoice")}</strong>
            <span>{t("externalSaleChoiceDescription")}</span>
          </button>
        </div>
      </div>
    );
  }

  function renderManualExternalSales() {
    return (
      <div className="manualExternalSales">
        <p className="manualExternalSalesDescription">{t("externalSaleDescription")}</p>
        <div className="manualExternalSalesRows">
          {manualExternalSaleRows.map((row, rowIndex) => {
            const ticket = allTickets.current.find((item) => item.id === row.ticketId);
            return (
              <div className="manualExternalSalesRow" key={row.id}>
                <div className="manualExternalSalesRowHeader">
                  <strong>{`${t("externalSaleRow")} ${rowIndex + 1}`}</strong>
                  {manualExternalSaleRows.length > 1 ? (
                    <button
                      type="button"
                      onClick={() =>
                        setManualExternalSaleRows((rows) => rows.filter((item) => item.id !== row.id))
                      }
                      disabled={isRecordingExternalSales}
                    >
                      {t("delete")}
                    </button>
                  ) : null}
                </div>
                <div className="manualExternalSalesFields">
                  <label>
                    <span>{t("externalSaleTicket")}</span>
                    <select
                      value={row.ticketId}
                      onChange={(event) => selectManualExternalTicket(row.id, event.target.value)}
                      disabled={isRecordingExternalSales}
                    >
                      <option value="">{t("externalSaleSelectTicket")}</option>
                      {allTickets.current.map((item) => (
                        <option key={item.id} value={item.id}>{item.name}</option>
                      ))}
                    </select>
                  </label>
                  <label className="manualExternalQuantity">
                    <span>{t("externalSaleQuantity")}</span>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={row.quantity}
                      onChange={(event) => updateManualExternalSaleRow(row.id, { quantity: event.target.value })}
                      disabled={isRecordingExternalSales}
                    />
                  </label>
                </div>
                {ticket?.options?.length ? (
                  <div className="manualExternalOptions">
                    {ticket.options.map((group) => {
                      const selectedIds = row.optionSelections
                        .filter((selection) => selection.groupId === group.id)
                        .map((selection) => selection.valueId);
                      return (
                        <fieldset key={group.id}>
                          <legend>{`${group.name}${group.required ? " *" : ""}`}</legend>
                          <div className="manualExternalOptionValues">
                            {(group.values || []).map((value) => {
                              const selected = selectedIds.includes(value.id);
                              const inputType = group.selectionType === "multi" ? "checkbox" : "radio";
                              const maxReached =
                                group.selectionType === "multi" &&
                                Number(group.maxSelect || 0) > 0 &&
                                selectedIds.length >= Number(group.maxSelect) &&
                                !selected;
                              return (
                                <label key={value.id} className={selected ? "selected" : ""}>
                                  <input
                                    type={inputType}
                                    name={`${row.id}-${group.id}`}
                                    checked={selected}
                                    disabled={isRecordingExternalSales || maxReached}
                                    onChange={() => toggleManualExternalOption(row.id, group, value.id)}
                                  />
                                  <span>{value.label}</span>
                                  {Number(value.priceDelta || 0) !== 0 ? (
                                    <em>{`${Number(value.priceDelta) > 0 ? "+" : ""}${Number(value.priceDelta).toFixed(2)} €`}</em>
                                  ) : null}
                                </label>
                              );
                            })}
                          </div>
                        </fieldset>
                      );
                    })}
                  </div>
                ) : null}
                {row.error ? <p className="manualExternalSalesError">{row.error}</p> : null}
              </div>
            );
          })}
        </div>
        <button
          type="button"
          className="manualExternalAddRow"
          onClick={() => setManualExternalSaleRows((rows) => [...rows, createManualExternalSaleRow()])}
          disabled={isRecordingExternalSales}
        >
          + {t("externalSaleAddRow")}
        </button>
      </div>
    );
  }

  function renderBroadcastModalContent() {
    return (
      <div className="broadcastMailForm">
        <p className="broadcastMailSummary">
          {t("broadcastSummary", {
            recipients: recipientCount ?? "…",
            duplicates: Math.max(0, (summary?.emailParticipants || 0) - (recipientCount || 0)),
            limit: DAILY_EMAIL_LIMIT,
          })}
        </p>
        <button
          type="button"
          className="broadcastSecondaryButton"
          onClick={exportUniqueBroadcastEmailsToCSV}
          disabled={isSendingBroadcast || !recipientCount}
        >
          {t("broadcastExportUniqueCsv")}
        </button>
        {broadcastError ? <p className="broadcastMailError">{broadcastError}</p> : null}
        {isSendingBroadcast ? (
          <div className="broadcastMailLoader" role="status" aria-live="polite">
            <span className="broadcastMailSpinner" />
            <span>{t("broadcastSendingStatus")}</span>
          </div>
        ) : null}
        <label>
          {t("broadcastSubject")}
          <input
            type="text"
            value={broadcastSubject}
            onChange={(e) => setBroadcastSubject(e.target.value)}
            maxLength={120}
            disabled={isSendingBroadcast}
          />
        </label>
        <label>
          {t("broadcastMessage")}
          <textarea
            value={broadcastMessage}
            onChange={(e) => setBroadcastMessage(e.target.value)}
            rows={8}
            disabled={isSendingBroadcast}
          />
        </label>
      </div>
    );
  }

  function renderParticipantContainer() {
    return (
      <div className={`${isSmallScreen ? "participantsSection" : "mainParticipantsContainer"}`}>
        <div className="mainParticipantsHeader">
          <p className="title">{t("participantList")}</p>
          <div className="d-flex gap-2 flex-wrap justify-content-end">
            <button disabled={!dashboard.listReady} onClick={exportParticipantsToCSV}>{t("performance.exportLocalCsv")}</button>
            <button disabled={exportJob?.status === 'requesting'} onClick={() => startExport('participants')}>{t("performance.exportFullEmail")}</button>
          </div>
        </div>
        <div className="ticketSalesContainer">
          <div className="scannedTicketsContainer">
            <p className="value">{ready ? ticketsScannedNumber : "—"}</p>
            <p className="label">{t("scannedTicketsCount")}</p>
          </div>
          <div className="notScannedTicketsContainer">
            <p className="value">{ready ? ticketsSalesNumber - ticketsScannedNumber : "—"}</p>
            <p className="label">{t("ticketsToScanCount")}</p>
          </div>
        </div>
        <div className="searchBar">
          <span className="searchIcon">🔍</span>
          <input
            className="searchInput"
            type="text"
            aria-label={t("performance.search")}
            placeholder={t("performance.search")}
            value={participantSearch}
            ref={searchFieldRef}
            onChange={findParticipants}
            disabled={!dashboard.listReady}
          />
        </div>

        <hr className="separator" />

        <div className="orderButtons">
          <button
            disabled={!dashboard.listReady}
            onClick={() => handleOrder("alphabetical")}
            aria-pressed={participantsOrder === "alphabetical"}
            className={`btn ${participantsOrder === "alphabetical" ? "active" : ""}`}
          >
            {t("alphabetic")}
          </button>

          <button
            disabled={!dashboard.listReady}
            onClick={() => handleOrder("isRead")}
            aria-pressed={participantsOrder === "isRead"}
            className={`btn ${participantsOrder === "isRead" ? "active" : ""}`}
          >
            {t("scanned")}
          </button>

          <button
            disabled={!dashboard.listReady}
            onClick={() => handleOrder("creationDate")}
            aria-pressed={participantsOrder === "creationDate"}
            className={`btn ${participantsOrder === "creationDate" ? "active" : ""}`}
          >
            {t("chronological")}
          </button>
        </div>

        <div className="participants">

          {dashboard.listLoading && <p role="status">{t('performance.loadingParticipants', { count: dashboard.loadedCount })}</p>}
          {dashboard.listError && <p role="alert">{t('performance.loadError')} <button onClick={() => dashboard.loadParticipants()}>{t('publicCheckout.refresh')}</button></p>}
          {participants.map((item, index) => (
            <Participant key={item.id} participant={item} event={event}
              backgroundColor={COLORS_PARTICIPANTS[index % COLORS_PARTICIPANTS.length]}
              fetchParticipants={fetchParticipants} onParticipantCancelled={handleParticipantCancelled}
              loadDetail={(id) => ticketingDashboardRequest('participant', { eventId, orgId: org.id, id })} />
          ))}
          {!dashboard.listLoading && !dashboard.listError && dashboard.listReady && participants.length === 0 && <p className="noParticipants">{t("noParticipants")}</p>}
          <nav className="dashboardPagination" aria-label={t('performance.pagination')}>
            <button disabled={dashboard.page === 0 || dashboard.listLoading} onClick={dashboard.previousPage}>{t('performance.previous')}</button>
            <span>{t('performance.page', { page: dashboard.page + 1 })}</span>
            <button disabled={!dashboard.nextCursor || dashboard.listLoading} onClick={dashboard.nextPage}>{t('performance.next')}</button>
          </nav>
        </div>
      </div>
    );
  }

  if (!organizationMatches) return (
    <div className="ticketingDashboardMainContainer">
      <section className="eventInformationContainer dashboardDataStatus dashboardContextStatus" role={eventError || eventOrgId ? 'alert' : 'status'}>
        <p>{t(eventError ? 'performance.eventUnavailable' : eventOrgId ? 'performance.organizationMismatch' : 'performance.loading')}</p>
        {(eventError || eventOrgId) && <button type="button" onClick={fetchEvent}>{t('publicCheckout.refresh')}</button>}
      </section>
    </div>
  );

  return (
    <div className="ticketingDashboardMainContainer">
      <div className="ticketingDashboardMainContainerInner">
        <ModalComponent
          isOpen={!!modalInformations.title}
          onClose={() => setModalInformations({ title: "", content: "", type: "" })}
          title={modalInformations.title}
          message={modalInformations.content}
          actionsComponent={
            modalInformations.type === "broadcastResult" ? renderBroadcastResultActions() : null
          }
        />

        <ModalComponent
          isOpen={isExternalImportChoiceOpen}
          onClose={() => setIsExternalImportChoiceOpen(false)}
          title={t("externalImportChoiceTitle")}
          actionsComponent={renderExternalImportChoice()}
          modalClassName="externalImportChoiceModal"
        />

        <ModalComponent
          isOpen={isManualExternalSaleOpen}
          onClose={() => !isRecordingExternalSales && setIsManualExternalSaleOpen(false)}
          onValidate={confirmManualExternalSales}
          title={t("externalSaleTitle")}
          actionsComponent={renderManualExternalSales()}
          shouldNotCloseOnOverlayClick={isRecordingExternalSales}
          hideCloseButton={isRecordingExternalSales}
          modalClassName="manualExternalSalesModal"
          validateLabel={isRecordingExternalSales ? t("externalSaleSaving") : t("externalSaleConfirm")}
        />

        <ModalComponent
          isOpen={isBroadcastModalOpen}
          onClose={() => !isSendingBroadcast && setIsBroadcastModalOpen(false)}
          onValidate={sendBroadcastEmails}
          title={t("broadcastTitle")}
          actionsComponent={renderBroadcastModalContent()}
          shouldNotCloseOnOverlayClick={isSendingBroadcast}
          modalClassName="broadcastMailModal"
          hideCloseButton={isSendingBroadcast}
        />

        <ModalComponent
          isOpen={!!externalImportPreview}
          onClose={() => {
            if (isImportingIds) return;
            setExternalImportPreview(null);
            setExternalTicketMappings({});
          }}
          onValidate={confirmExternalImport}
          title={t("externalImportTitle")}
          actionsComponent={renderExternalImportPreview()}
          shouldNotCloseOnOverlayClick={isImportingIds}
          modalClassName="externalImportModal"
          hideCloseButton={isImportingIds}
          validateLabel={isImportingIds ? t("importing") : t("externalImportConfirm")}
        />

        <CustomTicketModal
          show={!!customizeTicketTarget}
          onClose={onCustomizationModalClose}
          eventId={eventId}
          ticketId={customizeTicketTarget?.id || null}
          ticketName={customizeTicketTarget?.name || ""}
          customTicketConfiguration={
            customizeTicketTarget?.customTicketConfiguration || event.customTicketConfiguration
          }
        />

        <div className="eventInformationContainer">
          <div className="titleContainer">
            <button
              className="buttonBackContainer"
              onClick={() =>
                navigate("/dashboard/ticketing", { state: { selectedOrgId: org?.id } })
              }
            >
              <img src={BackImage} />
            </button>
            <div>{event?.name}</div>
          </div>

          <SocialNetworks eventId={eventId} />

          <div className="dashboardDataStatus" aria-live="polite">
            <span>{dashboard.summaryError ? t(dashboard.summaryErrorStatus === 401 || dashboard.summaryErrorStatus === 403 ? 'performance.accessDenied' : 'performance.loadError') : !ready ? (t('performance.loading')) : t('performance.updated', { date: new Date(summary.generatedAt).toLocaleTimeString() })}</span>
            {dashboard.summaryError && <button onClick={() => dashboard.loadSummary()} disabled={dashboard.summaryLoading}>{t('publicCheckout.refresh')}</button>}
          </div>
          {exportJob && <div className="dashboardDataStatus" role="status">
            <span>{t(`performance.export_${exportJob.status}`, { count: exportJob.processed || 0 })}</span>
            {exportJob.url && <a href={exportJob.url} download>{t('performance.download')}</a>}
            {['queued', 'working', 'email_failed'].includes(exportJob.status) && <button onClick={checkExportStatus}>{t('performance.checkExport')}</button>}
            {['failed', 'expired', 'email_failed'].includes(exportJob.status) && <button disabled={exportJob.status === 'requesting'} onClick={() => startExport(exportJob.kind)}>{t('publicCheckout.refresh')}</button>}
          </div>}
          <div className="amountContainer" aria-busy={!ready}>
            <div className="ticketsScanned">
              <p className="amount">{participantsNb ?? "—"}</p>
              <p className="title">{t("participantsNumber")}</p>
            </div>
            <div className="ticketsSold">
              <p className="amount">{ready ? paidCheckoutTicketsNumber : "—"}</p>
              <p className="title">{t("paidCheckoutTickets")}</p>
            </div>
            <div className="ticketsDistributed">
              <p className="amount">{ready ? ticketsSalesNumber : "—"}</p>
              <p className="title">{t("ticketsDistributed")}</p>
            </div>
            <div className="totalRevenue">
              <p className="amount">{ready ? receivedTicketSalesAmount.toFixed(2) + "€" : "—"}</p>
              <p className="title">{t("collectedMoney")}</p>
            </div>
            <div className="theoreticalRevenue">
              <p className="amount">{ready ? ticketsTheoreticalAmount.toFixed(2) + "€" : "—"}</p>
              <p className="title">{t("theoreticalRevenue")}</p>
            </div>
          </div>

          <AttendanceSummary event={event} summary={ready ? summary : null} />
          {ready && <PublicCheckoutIncidents eventId={eventId} org={org} refreshKey={refreshKey} />}

          <div className="chartContainer">
            <div className="graphTypes">
              <button
                className={chartType === "ticketsNb" ? "active" : ""}
                onClick={() => onChartTypeChange("ticketsNb")}
                disabled={!ready || isChartLoading}
              >
                {t("numberOfTickets")}
              </button>
              <button
                className={chartType === "saleTotal" ? "active" : ""}
                onClick={() => onChartTypeChange("saleTotal")}
                disabled={!ready || isChartLoading}
              >
                {t("totalSales")}
              </button>
              <button
                className={chartType === "ticketScannedPerDay" ? "active" : ""}
                onClick={() => onChartTypeChange("ticketScannedPerDay")}
                disabled={!ready || isChartLoading}
              >
                {t("ticketsScannedPerDay")}
              </button>
              <button
                className={chartType === "payouts" ? "active" : ""}
                onClick={() => onChartTypeChange("payouts")}
                disabled={!ready || isChartLoading}
              >
                {t("stripePayouts")}
              </button>
            </div>
            <div className="chartContainerInner">
              {!ready ? <div aria-hidden="true" /> : chartType === "payouts" ? (
                <div className="payoutSummary" aria-live="polite">
                  {payoutError ? <p className="payoutSummaryError">{payoutError}</p> : null}
                  {!isChartLoading && !payoutError && payouts.length === 0 ? (
                    <div className="payoutSummaryEmpty">
                      <EmptyState
                        title={t("noData")}
                        text={t("nothingToDisplayRightNow")}
                      />
                    </div>
                  ) : null}
                  {payouts.length > 0 ? (
                    <>
                      <div className="payoutList" role="list">
                        {payouts.map((payout) => (
                          <div className="payoutListRow" role="listitem" key={payout.payoutId}>
                            <span>
                              {payout.isFutureBalance
                                ? t("payoutFutureBalance")
                                : new Date(payout.date).toLocaleDateString()}
                            </span>
                            <span
                              className={`payoutStatus payoutStatus-${
                                payout.isFuturePayout
                                  ? "future"
                                  : String(payout.status || "unknown").toLowerCase()
                              }`}
                            >
                              {payout.isFuturePayout
                                ? t("payoutStatus_future")
                                : t(`payoutStatus_${String(payout.status || "unknown").toLowerCase()}`)}
                            </span>
                            <strong>{Number(payout.amount || 0).toFixed(2)} {payout.currency}</strong>
                            <span className="payoutArrivalDate">
                              {payout.bankArrivalDate
                                ? t("payoutArrival", { date: new Date(payout.bankArrivalDate).toLocaleDateString() })
                                : ""}
                            </span>
                          </div>
                        ))}
                      </div>
                      <div className="payoutTotalsRow">
                        {Object.entries(payoutSummary).flatMap(([currency, statuses]) =>
                          ["paid", "in_transit", "pending", "failed", "canceled"].map((status) => (
                            <div className="payoutTotal" key={`${currency}-${status}`}>
                              <span>{t(`payoutStatus_${status}`)}</span>
                              <strong>{(statuses[status] || 0).toFixed(2)} {currency}</strong>
                            </div>
                          )),
                        )}
                      </div>
                    </>
                  ) : null}
                </div>
              ) : (
                <ChartCard
                  data={chartData}
                  isLoading={isChartLoading}
                  yAxisUnit={chartType === "saleTotal" ? "€" : t("tickets")}
                  allowDecimals={chartType === "saleTotal"}
                />
              )}
              <div className="pickerDivContainer">
                <div className={`pickersDiv ${chartType === 'ticketScannedPerDay' ? 'has-scan-user' : ''}`}>
                  {chartType === 'ticketScannedPerDay' && <div className="pickerDiv scan-user-picker">
                    <label htmlFor="ticket-scan-user">{t('scanUser.label')}</label>
                    <select id="ticket-scan-user" className="form-control" value={scanUser}
                      onChange={event => setScanUser(event.target.value)} disabled={!ready || !summary?.scanUsers}>
                      <option value="">{t('scanUser.all')}</option>
                      {(summary?.scanUsers || []).map(user => <option key={JSON.stringify(user.uid)} value={JSON.stringify(user.uid)}>
                        {user.uid ? user.label || user.uid : t('scanUser.unknown')}
                      </option>)}
                    </select>
                    <p className="scan-user-hint">{t('scanUser.help')}</p>
                  </div>}
                  <div className="pickerDiv">
                    <label>{t("startDate")}</label>
                    <DatePicker
                      selected={startDate}
                      onChange={(date) => setStartDate(date)}
                      showTimeSelect
                      timeFormat="HH:mm"
                      timeIntervals={60}
                      dateFormat="Pp"
                      className="form-control"
                      popperClassName="datepicker-popper"
                      calendarClassName="datepicker-calendar"
                      disabled={!ready || isChartLoading}
                    />
                  </div>
                  <div className="pickerDiv">
                    <label>{t("endDate")}</label>
                    <DatePicker
                      selected={endDate}
                      onChange={(date) => setEndDate(date)}
                      showTimeSelect
                      timeFormat="HH:mm"
                      timeIntervals={60}
                      dateFormat="Pp"
                      className="form-control"
                      popperClassName="datepicker-popper"
                      calendarClassName="datepicker-calendar"
                      disabled={!ready || isChartLoading}
                    />
                  </div>
                </div>

                <GradientButton onPress={onValidateChartDates}>{t("validate")}</GradientButton>
              </div>
            </div>
            <div className="exportActions">
              <button
                className="subtleExportButton"
                onClick={generateSalesReport}
                disabled={isGeneratingSalesReport || !ready}
              >
                {isGeneratingSalesReport ? t("salesReportGenerating") : t("salesReportExport")}
              </button>
              <button
                className="subtleExportButton"
                onClick={exportTicketPaymentsToCSV}
                disabled={isExportingPayments || isExportingPayouts}
              >
                {isExportingPayments ? t("exporting") : t("exportTicketPaymentsCsv")}
              </button>
              <button
                className="subtleExportButton"
                onClick={exportStripePayoutsToCSV}
                disabled={isExportingPayments || isExportingPayouts}
              >
                {isExportingPayouts ? t("exporting") : t("exportPayoutsCsv")}
              </button>
            </div>
            {(isExportingPayments || isExportingPayouts || (chartType === "payouts" && isChartLoading)) && (
              <div className="exportStatus" role="status" aria-live="polite">
                <span className="spinner" />
                <span>
                  {chartType === "payouts" && isChartLoading
                    ? t("payoutsLoading")
                    : t("exportMayTakeTime")}
                </span>
              </div>
            )}
          </div>

          <div className="linkContainer">
            <p className="linkTitle">{t("yourSalesWebsiteLink")}</p>
            <a href={TICKETING_URL} target="_blank" className="link">
              {TICKETING_URL}
            </a>
          </div>

          <div className="codeContainer">
            <p className="codeTitle">{t("iframeTitle")}</p>
            <p className="codeDescription">{t("iframeDescription")}</p>
            <div className="codeWrapper">
              <code className="code">{IFRAME_CODE}</code>
              <OutlinedPinkButton onPressFunction={copyCodeHandler}>{t("copy")}</OutlinedPinkButton>
            </div>
          </div>

          <div className="participantsContainer">
            <div className="participantsTitleRow">
              <p className="participantsTitle">{t("participants")}</p>
              <button
                className="broadcastMailButton"
                onClick={() => {
                  setBroadcastError("");
                  setIsBroadcastModalOpen(true);
                }}
                disabled={!ready || isSendingBroadcast || !participantsNb}
              >
                {isSendingBroadcast ? t("broadcastSending") : t("broadcastOpenButton")}
              </button>
            </div>
            <button
              className="participantsButton"
              onClick={() => setParticipantsVisible(!participantsVisible)}
            >
              <div className="participantsButtonContainer">
                <Avatars
                  participants={summary?.avatars || []}
                  colorsPalette={COLORS_PARTICIPANTS}
                />
                <p>
                  {participantsNb} {t("participants")}
                </p>
              </div>
              <img
                src={BackImage}
                className="backArrow"
                style={{
                  transform: !participantsVisible
                    ? isSmallScreen
                      ? "rotate(90deg)"
                      : "rotate(180deg)"
                    : isSmallScreen
                      ? "rotate(270deg)"
                      : "rotate(0deg)",
                }}
              />
            </button>
          </div>

          <div className="customizationContainer">
            <p className="customizationTitle">{t("customizationTitle")}</p>
            <button
              className="customizationButton"
              onClick={() => setCustomizeTicketTarget({ type: "event" })}
            >
              <p>{t("customizationButton")}</p>
              <span
                className={`${event.customTicketConfiguration ? "customizeTicketIndicator" : "noCustomizeTicketIndicator"}`}
              >
                {event.customTicketConfiguration ? t("alreadyCustomized") : t("notYetCustomized")}
              </span>
            </button>
          </div>

          {participantsVisible && isSmallScreen && renderParticipantContainer()}

          <div className="ticketsContainer">
            <div className="ticketsTitleRow">
              <p className="ticketsTitle">{t("yourTickets")}</p>
              <div className="ticketImportActions">
                <button
                  className="ticketImportBtn"
                  onClick={exportTicketsToXlsx}
                  disabled={!allTickets.current.length}
                >
                  {t("exportTicketsDetailed")}
                </button>
                <input
                  ref={importIdsInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleExternalImportFile}
                  style={{ display: "none" }}
                />
                <button
                  className="ticketImportBtn"
                  onClick={() => setIsExternalImportChoiceOpen(true)}
                  disabled={!tickets.length || isImportingIds}
                >
                  {isImportingIds ? t("importing") : t("externalImportButton")}
                </button>
              </div>
            </div>
            <div className="tickets">
              <InfiniteScrollList
                fetchItems={loadTickets}
                renderItem={(ticket, index) => (
                  <Ticket
                    key={ticket.id}
                    collapsible
                    ticket={ticket}
                    paidCheckoutAmountSold={paidCheckoutTicketsById[ticket.id] || 0}
                    paidCheckoutOptionsSold={paidCheckoutOptionsByTicketId[ticket.id] || {}}
                    index={index}
                    onCustomizeTicket={() =>
                      setCustomizeTicketTarget({
                        type: "ticket",
                        id: ticket.id,
                        name: ticket.name,
                        customTicketConfiguration: ticket.customTicketConfiguration || null,
                      })
                    }
                    eventLink={
                      ticket.secret && `${ENV.hostname}/events/${event?.id}?ticketId=${ticket.id}`
                    }
                  />
                )}
                data={tickets}
                hasMore={ticketsHasMore}
              />
            </div>
          </div>
        </div>

        {participantsVisible && !isSmallScreen && renderParticipantContainer()}
      </div>

      <PromoCodeList
        eventId={eventId}
        orgId={org?.id}
        tickets={tickets}
        onPromoCodesChanged={dashboard.loadSummary}
      />
    </div>
  );
}

export default TicketingDashboard;
